import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import API from '../../api/api';
import { isResponseSuccess } from '../../utils/api';
import { getTokenData } from 'src/utils/api';
import { LOADING_STATUSES } from '../../constants/statuses';
import { showNotification } from '../../utils/notifications';

const CAMPAIGNS_ACTIONS = {
  SEARCH: 'campaigns/search',
  BAN: 'campaigns/ban',
};

export const searchCampaign = createAsyncThunk(
  CAMPAIGNS_ACTIONS.SEARCH,
  async ({ id }, { rejectWithValue }) => {
    try {
      const response = await API(getTokenData()).CAMPAIGNS.load({ id });

      if (isResponseSuccess(response.status) && response.data) {
        return response.data;
      }

      const errorMessage = response.data?.error || 'Campaign not found';
      return rejectWithValue(errorMessage);
    } catch (error) {
      return rejectWithValue(error.toString());
    }
  },
);

export const banCampaign = createAsyncThunk(
  CAMPAIGNS_ACTIONS.BAN,
  async ({ id, userId }, { rejectWithValue }) => {
    try {
      const response = await API(getTokenData()).CAMPAIGNS.ban({ userId, campaignId: id });

      if (isResponseSuccess(response.status)) {
        showNotification({
          type: 'success',
          text: 'Campaign banned successfully!',
        });
        return { id };
      }

      const errorMessage = response.data?.error || 'Error banning campaign';
      return rejectWithValue(errorMessage);
    } catch (error) {
      return rejectWithValue(error.toString());
    }
  },
);

const initialState = {
  campaigns: [],
  searchStatus: LOADING_STATUSES.IDLE,
  banStatus: LOADING_STATUSES.IDLE,
  error: null,
};

const campaignsSlice = createSlice({
  name: 'campaigns',
  initialState,
  reducers: {
    clearCampaigns: (state) => {
      state.campaigns = [];
      state.searchStatus = LOADING_STATUSES.IDLE;
      state.banStatus = LOADING_STATUSES.IDLE;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Search Campaign
    builder
      .addCase(searchCampaign.pending, (state) => {
        state.searchStatus = LOADING_STATUSES.LOADING;
        state.error = null;
      })
      .addCase(searchCampaign.fulfilled, (state, { payload }) => {
        const campaign = Array.isArray(payload) ? payload : [payload];
        state.campaigns = campaign;
        state.searchStatus = LOADING_STATUSES.SUCCEEDED;
        state.error = null;
      })
      .addCase(searchCampaign.rejected, (state, { payload }) => {
        state.campaigns = [];
        state.searchStatus = LOADING_STATUSES.FAILED;
        state.error = payload || 'Error searching for campaign';
      });

    // Ban Campaign
    builder
      .addCase(banCampaign.pending, (state) => {
        state.banStatus = LOADING_STATUSES.LOADING;
        state.error = null;
      })
      .addCase(banCampaign.fulfilled, (state, { payload }) => {
        state.campaigns = state.campaigns.filter((campaign) => campaign.id !== payload.id);
        state.banStatus = LOADING_STATUSES.SUCCEEDED;
        state.error = null;
      })
      .addCase(banCampaign.rejected, (state, { payload }) => {
        state.banStatus = LOADING_STATUSES.FAILED;
        state.error = payload || 'Error banning campaign';
      });
  },
});

export const {
  clearCampaigns,
} = campaignsSlice.actions;

export default campaignsSlice.reducer;
